/**
 * Search Drawer Controller (Predictive Search)
 * Built on the shared Drawer class.
 * Features debounced queries, response caching, AbortController cancellation,
 * skeleton loading states, and live rendering for products, collections & pages.
 */

document.addEventListener('DOMContentLoaded', function () {
  const drawerEl = document.getElementById('search-drawer');
  if (!drawerEl) return;

  const searchInput = drawerEl.querySelector('.js-search-input');
  const clearBtn = drawerEl.querySelector('.js-search-clear, [type="reset"]');
  const idleState = drawerEl.querySelector('.js-search-idle');
  const loadingState = drawerEl.querySelector('.js-search-loading');
  const resultsContainer = drawerEl.querySelector('.js-search-results');
  const emptyState = drawerEl.querySelector('.js-search-empty');
  const searchTermEl = drawerEl.querySelector('.js-search-term');
  const footerEl = drawerEl.querySelector('.js-search-footer');
  const viewAllText = drawerEl.querySelector('.js-search-view-all-text');
  const formEl = drawerEl.querySelector('form');

  let debounceTimer = null;
  let currentAbortController = null;
  const searchCache = new Map();

  // Initialize shared drawer instance
  const drawer = new Drawer(drawerEl, {
    name: 'search',
    id: 'search-drawer',
    focusElement: searchInput,
    onOpen: () => {
      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
          if (searchInput.value.length > 0) searchInput.select();
        }, 60);
      }
    },
    onClose: () => {
      if (currentAbortController) {
        currentAbortController.abort();
      }
    },
  });

  // -------------------------------------------------------------
  // Predictive Search Helpers
  // -------------------------------------------------------------

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function formatMoney(amountCents) {
    const num = (parseFloat(amountCents) || 0).toFixed(2).replace('.', ',');
    return `${num} €`;
  }

  function resetToIdle() {
    if (idleState) idleState.hidden = false;
    if (loadingState) loadingState.hidden = true;
    if (resultsContainer) {
      resultsContainer.hidden = true;
      resultsContainer.innerHTML = '';
    }
    if (emptyState) emptyState.hidden = true;
    if (footerEl) footerEl.hidden = true;
  }

  async function performSearch(query) {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      resetToIdle();
      return;
    }

    // Check client-side cache
    if (searchCache.has(trimmed)) {
      renderData(searchCache.get(trimmed), trimmed);
      return;
    }

    if (currentAbortController) {
      currentAbortController.abort();
    }
    currentAbortController = new AbortController();

    if (idleState) idleState.hidden = true;
    if (resultsContainer) resultsContainer.hidden = true;
    if (emptyState) emptyState.hidden = true;
    if (footerEl) footerEl.hidden = true;
    if (loadingState) loadingState.hidden = false;

    try {
      const suggestUrl = `/search/suggest.json?q=${encodeURIComponent(
        trimmed
      )}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=show`;

      const response = await fetch(suggestUrl, { signal: currentAbortController.signal });
      if (!response.ok) throw new Error('Suggest API failed');

      const data = await response.json();
      searchCache.set(trimmed, data);

      renderData(data, trimmed);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Predictive search error:', err);
      if (loadingState) loadingState.hidden = true;
      if (emptyState) {
        emptyState.hidden = false;
        if (searchTermEl) searchTermEl.textContent = trimmed;
      }
    }
  }

  function renderData(data, query) {
    const results = data.resources?.results;
    const products = results?.products || [];
    const collections = results?.collections || [];
    const pages = results?.pages || [];
    const totalCount = products.length + collections.length + pages.length;

    if (loadingState) loadingState.hidden = true;

    if (totalCount === 0) {
      if (emptyState) {
        emptyState.hidden = false;
        if (searchTermEl) searchTermEl.textContent = query;
      }
      if (resultsContainer) resultsContainer.hidden = true;
      if (footerEl) footerEl.hidden = true;
      return;
    }

    renderResults({ products, collections, pages, query });
  }

  function renderResults({ products, collections, pages, query }) {
    if (!resultsContainer) return;

    let html = '';

    // 1. Collections Chips (if present)
    if (collections.length > 0) {
      html += '<div class="predictive-search__group predictive-search__group--collections">';
      html += '<p class="predictive-search__group-title">Catégories</p>';
      html += '<div class="predictive-search__collections">';
      collections.forEach((col) => {
        html += `<a href="${escapeHtml(col.url)}" class="predictive-search__collection-chip">${escapeHtml(col.title)}</a>`;
      });
      html += '</div></div>';
    }

    // 2. Products List (Impact Layout: Vertically centered info, no vendor, regular price)
    if (products.length > 0) {
      html += '<div class="predictive-search__group predictive-search__group--products">';
      html += '<p class="predictive-search__group-title">Produits</p>';
      html += '<div class="predictive-search__items" role="list">';

      products.forEach((p) => {
        const imgUrl = p.image ? p.image : '';
        const priceFormatted = formatMoney(p.price);
        const comparePriceFormatted = p.compare_at_price ? formatMoney(p.compare_at_price) : '';
        const isSale = p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price);

        html += `
          <a href="${escapeHtml(p.url)}" class="predictive-search-result group">
            ${imgUrl ? `<img src="${escapeHtml(imgUrl)}" alt="${escapeHtml(p.title)}" loading="lazy">` : '<div class="predictive-search-result__placeholder"></div>'}
            <div class="predictive-search-result__info">
              <span class="predictive-search-result__title"><span class="reversed-link hover:show">${escapeHtml(p.title)}</span></span>
              <div class="predictive-search-result__price-wrap">
                <span class="price">${priceFormatted}</span>
                ${isSale ? `<span class="price price--compare line-through">${comparePriceFormatted}</span>` : ''}
              </div>
            </div>
          </a>
        `;
      });

      html += '</div></div>';
    }

    resultsContainer.innerHTML = html;
    resultsContainer.hidden = false;

    // Footer View All button (pushed to bottom of drawer)
    if (footerEl) {
      footerEl.hidden = false;
      if (viewAllText) {
        viewAllText.textContent = `Voir tous les résultats pour « ${query} »`;
      }
    }
  }

  // Input listener with debounce
  if (searchInput) {
    searchInput.addEventListener('input', function () {
      const val = this.value.trim();
      clearTimeout(debounceTimer);

      if (val.length === 0) {
        if (currentAbortController) currentAbortController.abort();
        resetToIdle();
        return;
      }

      debounceTimer = setTimeout(() => {
        performSearch(val);
      }, 200);
    });
  }

  // Form Reset / Clear button click
  if (formEl) {
    formEl.addEventListener('reset', function () {
      if (currentAbortController) currentAbortController.abort();
      resetToIdle();
      setTimeout(() => {
        if (searchInput) searchInput.focus();
      }, 10);
    });
  }

  // Expose global interface
  window.searchDrawer = {
    open: () => drawer.open(),
    close: () => drawer.close(),
    toggle: () => drawer.toggle(),
    updatePosition: () => drawer.updatePosition(),
    isOpen: () => drawer.isOpen,
    instance: drawer,
  };
});
